import React, { createContext, useContext, useCallback, useEffect, useState } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { loginRequest } from '../authConfig';
import config from '../config';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [studentId, setStudentId] = useState(null);

  const account = accounts[0];
  const username = account?.name || account?.username || '';
  // localAccountId is the most reliable MSAL field that maps to the Entra object ID (oid)
  const objectId = account?.localAccountId || account?.idTokenClaims?.oid || null;
  const displayName = account?.name || account?.idTokenClaims?.name || account?.username || 'Student';

  const login = useCallback(() => {
    instance.loginRedirect(loginRequest);
  }, [instance]);

  const logout = useCallback(() => {
    instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  }, [instance]);

  // Returns the current ID token, refreshing silently if needed.
  // The token is sent as Bearer on every API call so the backend can
  // extract the caller's identity from the validated JWT instead of
  // trusting a client-supplied objectId.
  const getToken = useCallback(async () => {
    if (!account) return null;
    try {
      const result = await instance.acquireTokenSilent({
        scopes: ['openid', 'profile'],
        account,
      });
      return result.idToken ?? null;
    } catch (err) {
      // Silent refresh failed – redirect for a fresh token.
      try {
        await instance.acquireTokenRedirect({ scopes: ['openid', 'profile'], account });
      } catch (redirectErr) {
        console.error('[Auth] Token acquisition failed:', redirectErr);
      }
      return null;
    }
  }, [instance, account]);

  useEffect(() => {
    const syncStudentRecord = async () => {
      if (!isAuthenticated) {
        setStudentId(null);
        return;
      }

      if (!objectId) {
        console.error('[StudentSync] Authenticated but objectId is null.');
        alert('Sign-in succeeded but your user ID could not be read. Please sign out and try again.');
        setStudentId(null);
        return;
      }

      try {
        const token = await getToken();
        const response = await fetch(config.endpoints.registerStudent, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ displayName }),
        });

        const responseText = await response.text();

        if (!response.ok) {
          throw new Error(`Registration API failed ${response.status}: ${responseText}`);
        }

        const result = JSON.parse(responseText);
        setStudentId(result.studentId ?? null);
      } catch (error) {
        console.error('[StudentSync] Failed:', error);
        alert(`Student registration failed: ${error.message}`);
        setStudentId(null);
      }
    };

    syncStudentRecord();
  }, [isAuthenticated, objectId, displayName, getToken]);

  const value = {
    studentId,
    objectId,
    isAuthenticated,
    username,
    login,
    logout,
    account,
    getToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
